/**
 * Maps reloaded AgentCore conversation events into chat messages that carry
 * ordered tool chevrons (`thinkingChevron` feature).
 *
 * Differs from the flag-off text-only mapping in `useConversationHistory` in
 * three ways:
 *  1. `toolUse` blocks are kept and become `tools` chat items, positioned by
 *     their index in `content[]` — that index is what reproduces the agent's
 *     generation order on reload.
 *  2. Tool-only messages are no longer dropped — they attach to the preceding
 *     assistant message so a preamble and its chevrons render as one unit.
 *  3. `toolResult` blocks (which arrive in a separate `role: "user"` event) are
 *     correlated back onto their `toolUse` by `toolUseId` in a second pass.
 */

import { v4 as uuidv4 } from "uuid";
import { ChatItem, Message } from "@/types";
import { ConversationEvent } from "../api/conversation/conversation";
import {
  ContentBlock,
  applyToolResults,
  chatItemsToText,
  collapseConsecutive,
  contentBlocksToChatItems,
  extractToolResults,
} from "@/utils/toolChevrons";

/** Envelope role that marks a message as the user's. */
const USER_ROLE = "user";

/**
 * Parsed content blocks for one event, used by the chevron-aware mapping.
 * `undefined` means the event carried no usable envelope.
 */
interface ParsedEventBlocks {
  role: string | undefined;
  blocks: ContentBlock[];
}

/**
 * Extracts the raw `content[]` blocks and role from an event's envelope.
 *
 * Unlike `tryUnwrapEnvelope`, this keeps `toolUse`/`toolResult` blocks — the
 * chevron feature needs them, and their position in the array is what
 * reproduces generation order on reload.
 */
function parseEventBlocks(
  event: ConversationEvent,
): ParsedEventBlocks | undefined {
  const conversational = (event.payload || []).find(
    (entry) => entry.conversational,
  )?.conversational as
    | { content?: { text?: string }; role?: string }
    | undefined;

  const rawContent = conversational?.content?.text || "";
  const rawRole = conversational?.role?.trim().toLowerCase();
  if (!rawContent) return undefined;

  const trimmed = rawContent.trim();

  // Not an envelope — treat the whole thing as a single text block.
  if (!trimmed.startsWith("{")) {
    return { role: rawRole, blocks: [{ text: rawContent }] };
  }

  try {
    const parsed = JSON.parse(trimmed);

    // A bare toolUse/toolResult object (no envelope wrapper).
    if (parsed.toolUse || parsed.toolResult) {
      return { role: rawRole, blocks: [parsed as ContentBlock] };
    }

    const role =
      typeof parsed.message?.role === "string"
        ? parsed.message.role.trim().toLowerCase()
        : rawRole;

    if (Array.isArray(parsed.message?.content)) {
      return { role, blocks: parsed.message.content as ContentBlock[] };
    }

    // JSON, but not a recognizable envelope — fall back to raw text.
    return { role: rawRole, blocks: [{ text: rawContent }] };
  } catch {
    return { role: rawRole, blocks: [{ text: rawContent }] };
  }
}

/**
 * Maps events into messages carrying ordered `chatItems` (chevron feature).
 *
 * Differs from the flag-off path in three ways:
 *  1. `toolUse` blocks are kept and become `tools` chat items, positioned by
 *     their index in `content[]`.
 *  2. Tool-only messages are no longer dropped — they attach to the preceding
 *     assistant message so a preamble and its chevrons render as one unit.
 *  3. `toolResult` blocks (which arrive in a separate `role: "user"` event) are
 *     correlated back onto their `toolUse` by `toolUseId` in a second pass.
 */
export function mapEventsToChevronMessages(
  events: ConversationEvent[],
): Message[] {
  const messages: Message[] = [];
  const allResults = new Map<string, "success" | "error">();
  /** Index in `messages` of the assistant message tool blocks should attach to. */
  let lastAssistantIndex = -1;

  for (const event of events) {
    const parsed = parseEventBlocks(event);
    if (!parsed) continue;

    const { role, blocks } = parsed;

    // Collect tool results from any event; correlation happens after the loop.
    const results = extractToolResults(blocks);
    results.forEach((status, id) => allResults.set(id, status));

    const items = contentBlocksToChatItems(blocks);
    if (items.length === 0) continue;

    const isUser = role === USER_ROLE;
    const hasText = items.some((item) => item.kind === "text");

    // A tool-only message belongs to the assistant turn that issued the calls,
    // regardless of the envelope role AgentCore assigned it.
    if (!hasText && lastAssistantIndex >= 0) {
      const target = messages[lastAssistantIndex];
      target.chatItems = mergeChatItems(target.chatItems ?? [], items);
      continue;
    }

    // A user message with no tool activity keeps the simple text shape.
    if (isUser && !items.some((item) => item.kind === "tools")) {
      const text = chatItemsToText(items);
      if (!text.trim()) continue;
      messages.push({
        id: event.eventId || `event_${uuidv4()}`,
        content: text,
        isUser: true,
      });
      // A genuine user prompt closes the previous assistant turn. Without this
      // boundary, a later tool-only assistant event attaches to that stale turn
      // and renders *above* the prompt that triggered it.
      lastAssistantIndex = -1;
      continue;
    }

    if (isUser) {
      // User-role event carrying tool blocks alongside text — rare; keep the
      // text as a user message and let the tools attach to the assistant turn.
      const text = chatItemsToText(items);
      const toolItems = items.filter((item) => item.kind === "tools");
      // Attach tools to the still-open assistant turn *before* pushing the user
      // text, so they land on the turn that issued them rather than after the
      // prompt that follows it.
      if (toolItems.length > 0 && lastAssistantIndex >= 0) {
        const target = messages[lastAssistantIndex];
        target.chatItems = mergeChatItems(target.chatItems ?? [], toolItems);
      }
      if (text.trim()) {
        messages.push({
          id: event.eventId || `event_${uuidv4()}`,
          content: text,
          isUser: true,
        });
        // Same turn boundary as above.
        lastAssistantIndex = -1;
      }
      continue;
    }

    messages.push({
      id: event.eventId || `event_${uuidv4()}`,
      content: chatItemsToText(items),
      isUser: false,
      chatItems: items,
    });
    lastAssistantIndex = messages.length - 1;
  }

  // Second pass: attribute tool results to their calls by toolUseId.
  for (const message of messages) {
    if (message.chatItems) {
      applyToolResults(message.chatItems, allResults);
    }
  }

  // Drop assistant messages that ended up with nothing renderable.
  return messages.filter(
    (message) =>
      message.content.trim().length > 0 || (message.chatItems?.length ?? 0) > 0,
  );
}

/**
 * Appends `incoming` items onto `existing`, merging adjacent tool groups so
 * consecutive same-tool calls collapse across event boundaries.
 */
function mergeChatItems(
  existing: ChatItem[],
  incoming: ChatItem[],
): ChatItem[] {
  const merged = [...existing];

  for (const item of incoming) {
    const last = merged[merged.length - 1];
    if (item.kind === "tools" && last?.kind === "tools") {
      merged[merged.length - 1] = {
        kind: "tools",
        calls: collapseConsecutive([...last.calls, ...item.calls]),
      };
      continue;
    }
    merged.push(item);
  }

  return merged;
}
